import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { Headset } from "lucide-react";

import { cn, getFileUrl } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { parseMessageBody } from "../utils/ticket-context";
import { DiagnosticsCard, GenerationCard } from "./TicketContextCards";
import type { SupportTicketMessage } from "../types";

const getInitials = (name?: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "?";
};

interface TicketMessageBubbleProps {
  message: SupportTicketMessage;
  dateLocale: Locale;
  /** Mesaj, ekrana bakan kullanıcıya mı ait — hizalamayı bu belirler */
  isOwn: boolean;
  /** Kendi mesajlarında gösterilecek avatar görseli */
  userImageUrl?: string | null;
  /** Aynı kişinin ardışık mesajlarında avatar tekrar edilmez */
  isGrouped?: boolean;
  /** Ardışık mesaj grubunun son mesajı — saat yalnızca burada gösterilir */
  isGroupEnd?: boolean;
  onImageClick: (url: string) => void;
}

export const TicketMessageBubble: React.FC<TicketMessageBubbleProps> = ({
  message,
  dateLocale,
  isOwn,
  userImageUrl,
  isGrouped,
  isGroupEnd = true,
  onImageClick,
}) => {
  const { t } = useTranslation();
  const fromSupport = message.isFromSupport;
  const images = message.images ?? [];
  const { text, diagnostics, generation } = parseMessageBody(message.body);

  const sentTime = format(new Date(message.sentAt), "HH:mm", {
    locale: dateLocale,
  });
  const authorLabel =
    message.authorName || (fromSupport ? t("support.myTickets.supportTeam") : "?");

  return (
    <div
      className={cn(
        "group/message flex w-full items-end gap-2 sm:gap-2.5",
        isOwn ? "flex-row-reverse" : "flex-row",
        isGrouped ? "mt-1" : "mt-3 first:mt-0 sm:mt-4",
      )}
    >
      {/* Avatar — gruplanmış mesajlarda yer tutucu ile hizalama korunur */}
      {isGrouped ? (
        <div className="h-7 w-7 shrink-0 sm:h-8 sm:w-8" />
      ) : isOwn ? (
        <Avatar className="h-7 w-7 shrink-0 border border-border sm:h-8 sm:w-8">
          <AvatarImage src={userImageUrl ?? undefined} alt={message.authorName} />
          <AvatarFallback className="text-[11px] font-semibold text-muted-foreground">
            {getInitials(message.authorName)}
          </AvatarFallback>
        </Avatar>
      ) : fromSupport ? (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-sm sm:h-8 sm:w-8">
          <Headset className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </div>
      ) : (
        <Avatar className="h-7 w-7 shrink-0 border border-border sm:h-8 sm:w-8">
          <AvatarFallback className="text-[11px] font-semibold text-muted-foreground">
            {getInitials(message.authorName)}
          </AvatarFallback>
        </Avatar>
      )}

      <div
        className={cn(
          "flex min-w-0 max-w-[88%] flex-col gap-1 sm:max-w-[78%] sm:gap-1.5",
          isOwn ? "items-end" : "items-start",
        )}
      >
        {/* Gönderen adı + rozet + saat — yalnızca grup başında tek satır */}
        {!isGrouped && (
          <span
            className={cn(
              "flex items-center gap-1.5 px-1 text-xs text-muted-foreground",
              isOwn && "flex-row-reverse",
            )}
          >
            <span className="max-w-[180px] truncate font-medium">
              {authorLabel}
            </span>
            {fromSupport && message.authorName && (
              // Cevabın destek ekibi adına gittiği/geldiği her iki tarafta da
              // net olmalı — yazar adının yanına rozet basılır
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-indigo-500/10 px-1.5 py-px text-[10px] font-semibold text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400">
                <Headset className="h-2.5 w-2.5" />
                {t("support.myTickets.supportTeam")}
              </span>
            )}
            <span className="shrink-0 text-[11px] text-muted-foreground/70">
              {sentTime}
            </span>
          </span>
        )}

        {text && (
          <div
            className={cn(
              "w-fit whitespace-pre-wrap break-words px-3.5 py-2 text-sm leading-relaxed shadow-sm sm:px-4 sm:py-2.5",
              isOwn
                ? "rounded-2xl rounded-br-md bg-gradient-to-br from-indigo-600 to-violet-600 text-white dark:from-indigo-500 dark:to-violet-500"
                : "rounded-2xl rounded-bl-md bg-muted text-foreground",
            )}
          >
            {text}
          </div>
        )}

        {/* Mesaja iliştirilmiş bağlam */}
        {generation && (
          <GenerationCard generation={generation} onImageClick={onImageClick} />
        )}
        {diagnostics && <DiagnosticsCard diagnostics={diagnostics} />}

        {/* Görsel galerisi — tek görsel büyük, çoklu görsel ızgara */}
        {images.length > 0 && (
          <div
            className={cn(
              "grid gap-1.5",
              images.length === 1 ? "grid-cols-1" : "grid-cols-2",
            )}
          >
            {images.map((image) => {
              const url = getFileUrl(image);
              if (!url) return null;
              return (
                <button
                  key={image}
                  type="button"
                  onClick={() => onImageClick(url)}
                  className={cn(
                    "group relative overflow-hidden rounded-xl border border-border bg-muted",
                    images.length === 1 ? "h-44 w-44" : "h-24 w-24",
                  )}
                >
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/10" />
                </button>
              );
            })}
          </div>
        )}

        {/* Saat: grup başında zaten isim satırında var; gruplanmış mesajlarda
            yalnızca son mesajda görünür, aradakiler hover ile okunur */}
        {isGrouped && (
          <span
            className={cn(
              "px-1 text-[11px] text-muted-foreground/70",
              !isGroupEnd &&
                "opacity-0 transition-opacity group-hover/message:opacity-100",
            )}
          >
            {sentTime}
          </span>
        )}
      </div>
    </div>
  );
};
